import { useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import LaunchCard from "../LaunchCard.jsx";

// X-Ray › Workflows panel — the run-gated body for the workflow miner.
// Idle shows a LaunchCard (explicit spend, like Describe); running shows
// phase progress; done renders the candidate workflows: each a recurring
// shape across the scope's sessions, with verified members (openable) and a
// candidate artifact for human review. Both the skill and the tool are
// generated per run, so the header toggle flips between them instantly —
// no up-front choice needed. Skill is the default — a
// procedure-with-judgment playbook fits most agent workflows better than
// a single parameterized call. Nothing here is executed or installed.

const PHASE_LABEL = {
  summarize: "Reading sessions",
  cluster: "Grouping workflows",
  verify: "Verifying clusters",
  suggest: "Drafting candidates",
};

function FilterChip({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[6px] py-[2px] text-[10px] leading-[1.2] uppercase tracking-[0.04em] text-[color:var(--muted)] transition-colors cursor-pointer hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
    >
      {label}
    </button>
  );
}

function ArtifactToggle({ value, onChange }) {
  return (
    <span className="inline-flex shrink-0 overflow-hidden rounded-[2px] border border-[color:var(--line)]">
      {[
        ["skill", "Skill"],
        ["tool", "Tool"],
      ].map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={[
            "px-[8px] py-[2px] text-[10px] uppercase tracking-[0.04em] cursor-pointer transition-colors",
            value === k ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
          ].join(" ")}
        >
          {label}
        </button>
      ))}
    </span>
  );
}

function ClassChip({ classification }) {
  const sideEffecting = classification === "side-effecting";
  const readOnly = classification === "read-only";
  return (
    <span
      className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
      style={
        sideEffecting
          ? { borderColor: "var(--red,#c0392b)", color: "var(--red,#c0392b)" }
          : readOnly
            ? { borderColor: "var(--green)", color: "var(--ink)", backgroundColor: "var(--green-soft)" }
            : { borderColor: "var(--line)", color: "var(--muted)" }
      }
      title={
        readOnly
          ? "Reads only. Eligible for a kernel draft."
          : sideEffecting
            ? "Changes state on endpoints. Review before creating a skill."
            : "Side effects have not been determined."
      }
    >
      {classification}
    </span>
  );
}

function StepList({ steps }) {
  if (!steps?.length) return null;
  return (
    <ol className="m-0 grid min-w-0 list-none gap-[2px] p-0">
      {steps.map((step, i) => (
        <li key={i} className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-[8px] text-[11.5px] leading-[1.45] text-[color:var(--ink)]">
          <span className="tabular-nums text-[color:var(--muted-soft)]">{i + 1}.</span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{step}</span>
        </li>
      ))}
    </ol>
  );
}

function MetaLine({ label, items }) {
  if (!items?.length) return null;
  return (
    <span className="min-w-0 text-[10.5px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]">
      <span className="text-[color:var(--muted-soft)]">{label}: </span>
      {items.join(", ")}
    </span>
  );
}

// SKILL draft — frontmatter preview (name + description, the .skill shape)
// then the playbook body.
function SkillBody({ skill }) {
  if (!skill) return null;
  return (
    <div className="grid min-w-0 gap-[5px]">
      <span className="text-[9px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Candidate skill (draft)</span>
      <pre className="m-0 min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[11px] leading-[1.5] text-[color:var(--ink)]">
        {`name: ${skill.name || "workflow-skill"}\ndescription: ${skill.whenToUse || "—"}`}
      </pre>
      <StepList steps={skill.instructions} />
      <MetaLine label="inputs" items={skill.inputs} />
      <MetaLine label="uses" items={skill.usesTools} />
    </div>
  );
}

// TOOL sketch — canonical steps then the pseudo-code callable.
function ToolBody({ tool }) {
  if (!tool) return null;
  return (
    <div className="grid min-w-0 gap-[5px]">
      <StepList steps={tool.representativeSteps} />
      <span className="text-[9px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Candidate tool (untested)</span>
      {tool.pseudocode ? (
        <pre className="m-0 min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[11px] leading-[1.5] text-[color:var(--ink)]">
          {tool.pseudocode}
        </pre>
      ) : null}
      <MetaLine label="params" items={tool.paramSurface} />
    </div>
  );
}

function WorkflowCard({ wf, artifact, onSelectTrace }) {
  return (
    <div className="grid min-w-0 gap-[8px] border-b border-[color:var(--line-soft)] px-[12px] py-[11px] last:border-b-0">
      <div className="flex min-w-0 items-baseline justify-between gap-[10px]">
        <span className="min-w-0 truncate text-[13px] font-medium leading-[1.3] text-[color:var(--ink)]">{wf.name}</span>
        <span className="flex shrink-0 items-center gap-[6px]">
          <ClassChip classification={wf.classification} />
          <span className="text-[10px] tabular-nums text-[color:var(--muted)]">
            {wf.coverage} session{wf.coverage === 1 ? "" : "s"}
          </span>
        </span>
      </div>
      {wf.description ? (
        <span className="min-w-0 text-[12px] leading-[1.5] text-[color:var(--muted)] [overflow-wrap:anywhere]">{wf.description}</span>
      ) : null}

      {artifact === "tool" ? <ToolBody tool={wf.tool} /> : <SkillBody skill={wf.skill} />}

      {wf.members?.length ? (
        <div className="grid min-w-0 gap-[2px]">
          <span className="text-[9px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Members</span>
          <div className="grid min-w-0 gap-[1px]">
            {wf.members.map((m) => (
              <button
                key={m.sessionId}
                type="button"
                onClick={() => onSelectTrace?.(m.promptId)}
                className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)] px-[2px] py-[3px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
              >
                <span className="truncate text-[11px] leading-[1.3] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                  {m.label || m.sessionId}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function WorkflowsPanel({ run, sessionsCount, windowKey, onRun, onSelectTrace, modelControl = null }) {
  // Skill is the default artifact — agent workflows are usually
  // procedures-with-judgment, which a skill captures more faithfully than
  // a single deterministic tool call. Both artifacts are generated per run,
  // so this toggle is an instant re-render, not a re-run.
  const [artifact, setArtifact] = useState("skill");

  if (run.status === "running") {
    const p = run.progress;
    const label = p ? PHASE_LABEL[p.phase] || p.phase : "Starting";
    return (
      <div className="grid gap-[6px] px-[12px] py-[10px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {label}
          {p && p.total ? ` ${p.done}/${p.total}` : "…"}
        </span>
      </div>
    );
  }

  if (run.status === "error") {
    return (
      <div className="grid justify-items-start gap-[8px] px-[12px] py-[10px]">
        <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words">{run.error}</span>
        <FilterChip label="Retry" onClick={onRun} />
      </div>
    );
  }

  if (run.status === "done" && run.data) {
    const stale = run.forKey !== windowKey;
    const { workflows, sessionCount, scannedCount, summarizedCount, droppedForCap = 0, unclustered = 0 } = run.data;
    const scanned = scannedCount ?? sessionCount;
    const capNote = droppedForCap ? ` · ${droppedForCap} not read due to the ${scanned}-session limit` : "";
    if (!workflows.length) {
      return (
        <div className="grid justify-items-start gap-[8px] px-[12px] py-[10px]">
          <span className={NOTE_CLASS}>
            No recurring workflows surfaced across {summarizedCount} of {scanned} sessions{capNote}. Scrub to busier traffic or widen the window.
          </span>
          <FilterChip label={stale ? "Rescan current window" : "Rescan"} onClick={onRun} />
        </div>
      );
    }
    const extras = [];
    if (unclustered) extras.push(`${unclustered} unclustered`);
    if (droppedForCap) extras.push(`${droppedForCap} beyond cap`);
    return (
      <div className="grid min-w-0 max-w-full overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-[8px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
          <span className={`${NOTE_CLASS} min-w-0 flex-1 truncate`}>
            {workflows.length} candidate workflow{workflows.length === 1 ? "" : "s"} · read from {summarizedCount} of {scanned} sessions
            {extras.length ? ` · ${extras.join(" · ")}` : ""}
          </span>
          <div className="flex shrink-0 items-center gap-[8px]">
            <ArtifactToggle value={artifact} onChange={setArtifact} />
            <FilterChip label={stale ? "Rescan current window" : "Rescan"} onClick={onRun} />
          </div>
        </div>
        {stale ? (
          <span className={`${NOTE_CLASS} border-b border-[color:var(--line-soft)] px-[12px] py-[5px]`}>scanned a previous window</span>
        ) : null}
        {workflows.map((wf) => (
          <WorkflowCard key={wf.id} wf={wf} artifact={artifact} onSelectTrace={onSelectTrace} />
        ))}
      </div>
    );
  }

  return (
    <div className="px-[12px] py-[10px]">
      <LaunchCard
        eyebrow="Workflows"
        description="Find recurring workflows in this window and draft a skill and tool for each one. This sends full session bodies to the selected model."
        controls={
          modelControl ? (
            <div className="flex items-center gap-[8px] min-w-0">
              <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
              {modelControl}
            </div>
          ) : null
        }
        ctaLabel={`Mine workflows · ${sessionsCount} session${sessionsCount === 1 ? "" : "s"}`}
        onLaunch={onRun}
      />
    </div>
  );
}
